import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shown persistently from page load; only hides near the footer.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;
  #buyNowInProgress = false;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    // The number in the bar's quantity stepper is a picker (<select>). The listener sits on the
    // component itself, so it keeps working after the bar is re-rendered on a variant change.
    this.addEventListener('change', this.#handleQtyChange, { signal });

    // The list of numbers depends on stock and on what is already in the cart, so refresh it when
    // the cart changes (the page's quantity box updates its own cart count first).
    document.addEventListener(ThemeEvents.cartUpdate, this.#scheduleQtyRefresh, { signal });

    this.#getInitialQuantity();
    this.#scheduleQtyRefresh();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    window.removeEventListener('resize', this.#updateOverlapClearance);
    document.documentElement.style.removeProperty('--sticky-add-to-cart-clearance');
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the sticky bar: shown persistently from page load (Noon-style),
   * only hiding when the footer approaches so it doesn't overlap it.
   * The main page's Add to Cart / Buy Now buttons are hidden via CSS now
   * (purchase actions live only in this bar), so there's no longer a
   * separate "reveal after scrolling past the buy box" state to manage.
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    // Always visible from the moment the page loads.
    this.#showStickyBar();

    // Prefer a semantic <footer>, but fall back to the footer section-group
    // wrapper. Some footer configurations render as a <div> rather than a
    // <footer> element, and requiring a literal <footer> here used to abort
    // this entire function - silently disabling the sticky bar altogether,
    // not just the footer-proximity refinement below.
    const footer = document.querySelector('footer') || document.querySelector('.shopify-section-group-footer-group');
    if (!footer) return;

    // Observer for footer visibility - hides sticky bar at page bottom,
    // and re-shows it if the visitor scrolls back away from the footer.
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (this.#hiddenByBottom) {
          this.#hiddenByBottom = false;
          this.#showStickyBar();
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#mainBottomObserver.observe(footer);
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles the quantity increase button click in the sticky bar.
   * Drives the real product-page quantity selector rather than keeping a
   * separate value, so the quantity actually submitted with the form
   * (via handleAddToCartClick's puppet click) always stays correct.
   */
  handleIncreaseQuantity = () => {
    this.#getMainQuantitySelector()?.updateQuantity(1);
  };

  /**
   * Handles the quantity decrease button click in the sticky bar.
   */
  handleDecreaseQuantity = () => {
    this.#getMainQuantitySelector()?.updateQuantity(-1);
  };

  /**
   * Handles a tap on a variant chip in the sticky bar by driving the real variant picker
   * on the product page, so price, stock, media, the form and this bar all update through
   * the theme's normal variant-change flow.
   * @param {Event} event
   */
  handleVariantChipClick = (event) => {
    const chip = event.target instanceof Element ? event.target.closest('[data-option-value-id]') : null;
    if (!(chip instanceof HTMLElement) || chip.getAttribute('aria-checked') === 'true') return;

    const section = this.closest('.shopify-section');
    const picker =
      section?.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`) ??
      section?.querySelector('variant-picker');
    if (!picker) return;

    const valueId = chip.dataset.optionValueId;
    const radio = picker.querySelector(`input[data-option-value-id="${valueId}"]`);
    if (radio instanceof HTMLInputElement) {
      radio.click();
    } else {
      // Dropdown-style pickers use a <select> instead of radio buttons
      const option = picker.querySelector(`option[data-option-value-id="${valueId}"]`);
      const select = option?.closest('select');
      if (!(option instanceof HTMLOptionElement) || !select) return;
      select.value = option.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Show the new choice straight away; the variant update that follows re-renders the bar
    chip.parentElement?.querySelectorAll('[data-option-value-id]').forEach((el) => {
      el.setAttribute('aria-checked', String(el === chip));
    });
  };

  /**
   * Returns the bar's quantity picker if the event came from it.
   * @param {Event} event
   * @returns {HTMLSelectElement | null}
   */
  #qtyPickerFrom(event) {
    const target = event.target;
    return target instanceof HTMLSelectElement && target.matches('[ref="stickyQuantityValue"]') ? target : null;
  }

  /**
   * Applies the number picked in the bar through the page's real quantity selector, which snaps it
   * to the allowed minimum, maximum and step, then shows the value that was actually accepted.
   */
  #handleQtyChange = (/** @type {Event} */ event) => {
    const picker = this.#qtyPickerFrom(event);
    if (!picker) return;

    const selector = /** @type {any} */ (this.#getMainQuantitySelector());
    const mainInput = selector?.querySelector('input[ref="quantityInput"]');
    if (!selector || !(mainInput instanceof HTMLInputElement)) return;

    mainInput.value = picker.value;
    selector.setQuantity({ target: mainInput, preventDefault() {} });

    this.#currentQuantity = parseInt(mainInput.value, 10) || 1;
    this.#updateButtonText();
  };

  /** @type {number | undefined} */
  #qtyRefreshTimer;

  /**
   * Refreshes the picker shortly after a cart or variant change, once the page's own quantity box
   * has updated its limits and cart count.
   */
  #scheduleQtyRefresh = () => {
    clearTimeout(this.#qtyRefreshTimer);
    this.#qtyRefreshTimer = window.setTimeout(() => this.#refreshQuantityPicker(), 250);
  };

  /**
   * Rebuilds the picker's numbers from the page's quantity box: from the minimum, in steps, up to
   * what can still be added (stock minus what is already in the cart), at most 99. If the current
   * choice is above the new limit it is brought down to it.
   */
  #refreshQuantityPicker() {
    const picker = this.querySelector('[ref="stickyQuantityValue"]');
    const selector = /** @type {any} */ (this.#getMainQuantitySelector());
    if (!(picker instanceof HTMLSelectElement) || !selector?.getCurrentValues) return;

    const { min, step } = selector.getCurrentValues();
    const effectiveMax = selector.getEffectiveMax();
    const top = Math.max(min, Math.min(effectiveMax ?? 99, 99));

    // Bring an over-limit choice down to the limit (this also tells the rest of the page)
    if (parseInt(selector.getValue(), 10) > top) selector.updateQuantity(0);

    const numbers = [];
    for (let n = min; n <= top; n += step) numbers.push(n);

    const current = String(parseInt(selector.getValue(), 10) || min);
    const wanted = numbers.map(String).join(',');
    const existing = Array.from(picker.options)
      .map((o) => o.value)
      .join(',');

    if (wanted !== existing) {
      picker.replaceChildren(
        ...numbers.map((n) => {
          const option = document.createElement('option');
          option.value = String(n);
          option.textContent = String(n);
          return option;
        })
      );
    }
    picker.value = current;
    this.#currentQuantity = parseInt(current, 10) || min;
    this.#syncStepperButtons();
  }

  /** Greys out + and - when the number is at the stock limit or the minimum. */
  #syncStepperButtons() {
    const selector = /** @type {any} */ (this.#getMainQuantitySelector());
    if (!selector?.getCurrentValues) return;
    const { min, value } = selector.getCurrentValues();
    const effectiveMax = selector.getEffectiveMax();
    const minus = this.querySelector('[ref="stickyMinusButton"]');
    const plus = this.querySelector('[ref="stickyPlusButton"]');
    if (minus instanceof HTMLButtonElement) minus.disabled = value <= min;
    if (plus instanceof HTMLButtonElement) plus.disabled = effectiveMax !== null && value >= effectiveMax;
  }

  /**
   * Handles the Buy Now button click in the sticky bar.
   *
   * It first tries to puppet-click the real dynamic checkout button on the main product form.
   * Shopify renders that button inside a shadow root the theme cannot reach (and the block is
   * hidden by CSS), so in practice there is nothing to click and the old code did nothing at all.
   * When no button can be clicked, the selected variant and quantity are added to the cart and the
   * shopper is sent straight to checkout. Anything already in the cart goes to checkout with it.
   */
  handleBuyNowClick = async () => {
    const acceleratedButton = this.#getAcceleratedCheckoutButton();
    if (acceleratedButton) {
      acceleratedButton.click();
      return;
    }

    if (this.#buyNowInProgress) return;

    const form = this.#getProductForm();
    const variantId = form?.querySelector('[name="id"]')?.value;
    const quantity = parseInt(form?.querySelector('[name="quantity"]')?.value || '1', 10) || 1;
    if (!variantId) return;

    const root = window.Shopify?.routes?.root ?? '/';
    const buyNowButton = this.querySelector('[ref="stickyBuyNowButton"]');

    this.#buyNowInProgress = true;
    if (buyNowButton instanceof HTMLButtonElement) {
      buyNowButton.disabled = true;
      buyNowButton.setAttribute('aria-busy', 'true');
    }

    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity }] }),
      });

      // On success go to checkout. If the item could not be added (for example the stock changed),
      // open the cart so the shopper can see why instead of leaving the button looking dead.
      window.location.href = `${root}${response.ok ? 'checkout' : 'cart'}`;
    } catch (error) {
      this.#buyNowInProgress = false;
      if (buyNowButton instanceof HTMLButtonElement) {
        buyNowButton.disabled = false;
        buyNowButton.removeAttribute('aria-busy');
      }
    }
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();

    // The new variant has its own stock, so rebuild the number picker once the page has applied it
    this.#scheduleQtyRefresh();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
    this.#updateOverlapClearance();
    window.addEventListener('resize', this.#updateOverlapClearance);
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
    document.documentElement.style.removeProperty('--sticky-add-to-cart-clearance');
    window.removeEventListener('resize', this.#updateOverlapClearance);
  }

  /**
   * Measures the sticky bar's actual on-screen position (however tall it is,
   * on whatever breakpoint, with whatever bottom offset currently applies -
   * e.g. Shopify's own theme-preview toolbar nudges it up too) and exposes
   * the space it occupies as a CSS variable, so any other fixed-position
   * element on the page (e.g. a WhatsApp launcher button coded directly
   * into the theme) can read it and automatically clear the bar instead of
   * using a hardcoded offset that would drift out of sync if this bar's
   * height or position ever changes.
   */
  #updateOverlapClearance = () => {
    const measure = () => {
      if (!this.#isStuck) return;
      const rect = this.refs.stickyBar.getBoundingClientRect();
      const clearance = Math.max(0, Math.round(window.innerHeight - rect.top));
      document.documentElement.style.setProperty('--sticky-add-to-cart-clearance', `${clearance}px`);
    };

    // Measure once immediately (covers resize calls, and prefers-reduced-motion
    // where there's no transition to wait for), then again once the bar's
    // show/hide transition actually finishes - otherwise this can capture a
    // mid-slide-in position instead of where the bar actually comes to rest.
    measure();
    this.refs.stickyBar.addEventListener('transitionend', measure, { once: true });
  };

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the real quantity-selector-component on the main product page
   * (not inside the sticky bar), so the sticky bar's +/- buttons can drive
   * the single source of truth for quantity.
   * @returns {(HTMLElement & { updateQuantity: (stepMultiplier: number) => void }) | null}
   */
  #getMainQuantitySelector() {
    const sectionElement = this.closest('.shopify-section');
    return sectionElement?.querySelector('quantity-selector-component[ref="quantitySelector"]') ?? null;
  }

  /**
   * Gets the real dynamic checkout ("Buy now") button rendered on the main
   * product form, so the sticky bar's Buy Now button can puppet-click it.
   * @returns {HTMLButtonElement | null}
   */
  #getAcceleratedCheckoutButton() {
    const sectionElement = this.closest('.shopify-section');
    const block = sectionElement?.querySelector('.accelerated-checkout-block');
    return block?.querySelector('button') ?? null;
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }

    // Keep the sticky bar's own quantity stepper display in sync too
    const stickyQuantityValue = this.querySelector('[ref="stickyQuantityValue"]');
    if (stickyQuantityValue instanceof HTMLSelectElement) {
      // Make sure the picked number exists in the list (the list is refreshed separately)
      const value = this.#currentQuantity.toString();
      if (!Array.from(stickyQuantityValue.options).some((o) => o.value === value)) {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = value;
        stickyQuantityValue.appendChild(option);
      }
      stickyQuantityValue.value = value;
    } else if (stickyQuantityValue) {
      stickyQuantityValue.textContent = this.#currentQuantity.toString();
    }

    this.#syncStepperButtons();
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
